import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import LiveTv from '@mui/icons-material/LiveTv';
import BrandingWatermark from '@mui/icons-material/BrandingWatermark';
import Hub from '@mui/icons-material/Hub';
import MenuBook from '@mui/icons-material/MenuBook';
import { useTheme } from '@material-ui/core/styles';
import { readableHeading } from '../readablePalette';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';


const rows = [
  {
    servicio: 'Exposición audiovisual',
    precioUsd: 95,
    detalle: [
      'Spot de 20 s (antes/después) en la programación de TNO.',
      '2 rotativas semanales en antena.',
      'Micros de radio 45–60 s — eje negocio en familia y formalización.',
      'Micros de radio 45–60 s — eje redes, jóvenes y PI cotidiana, con cierre Hoet.',
      'Los guiones íntegros van en el paquete al cliente.',
    ],
    beneficio: 'Alta frecuencia y recordación de marca.',
    Icon: LiveTv,
  },
  {
    servicio: 'Integración de marca',
    precioUsd: 85,
    detalle: [
      'Logo o eslogan en presentación y cierre del programa.',
      '3 inserts visuales por episodio.',
      'Guía en antena con la Dra. Davogustto — bloque marca y negocio familiar: gancho, mensaje Hoet y gesto concreto.',
      'Bloque redes, jóvenes y derechos de autor: misma estructura (gancho, Hoet, gesto).',
      'Bloque herencia intangible y activos digitales: misma estructura (gancho, Hoet, gesto).',
      'La pauta completa se entrega en el paquete al cliente.',
    ],
    beneficio: 'Asociación directa con la ética de la Dra. Davogustto.',
    Icon: BrandingWatermark,
  },
  {
    servicio: 'Dominio digital',
    precioUsd: 65,
    detalle: [
      'Banner premium en el home de TNO.',
      'Logo en 2 Reels o Shorts semanales.',
      'Insert o cintillo 1: idea y patrimonio con presencia Hoet & Partners en señal.',
      'Insert o cintillo 2: PI explicada en lenguaje familiar, sin tecnicismo vacío.',
      'Insert o cintillo 3: expertos y acompañamiento legal en cada paso.',
      'El copy final para pantalla se aprueba con marca y se entrega en el kit al cierre del acuerdo.',
    ],
    beneficio: 'Vida perpetua: el contenido permanece como consulta recurrente.',
    Icon: Hub,
  },
  {
    servicio: 'SEO y autoridad',
    precioUsd: 45,
    detalle: [
      '1 artículo semanal en TNO Digest con enfoque SEO, firmado por Hoet & Partners.',
      'PI en negocios familiares y riesgos frecuentes.',
      'Publicar en redes, titularidad y guía para hogares.',
      'Herencia digital y activos intangibles como patrimonio.',
      'Titulares y cuerpo del artículo van en el entregable comercial, no en esta vista.',
    ],
    beneficio: 'Posicionamiento en Google para búsquedas de propiedad intelectual.',
    Icon: MenuBook,
  },
];

const TOTAL_USD = rows.reduce((s, r) => s + r.precioUsd, 0);

const PlanOroInvestment = () => {
  const theme = useTheme();
  const h = readableHeading(theme);
  const deep = theme.palette.primary.main;
  const gold = theme.palette.secondary.main;

  return (
    <Box component="section" sx={{ my: { xs: 5, md: 6 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 4,
            minHeight: { xs: 48, sm: 56 },
            borderRadius: 2,
            background: `linear-gradient(180deg, ${gold} 0%, ${deep} 100%)`,
            flexShrink: 0,
          }}
        />
        <Box>
          <Typography
            variant="overline"
            sx={{ color: gold, fontWeight: 700, letterSpacing: '0.16em', display: 'block', mb: 0.5 }}
          >
            Inversión sugerida
          </Typography>
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, color: h, fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.2 }}
          >
            Plan Oro · patrocinante exclusivo
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              mt: 1.25,
              fontWeight: 800,
              color: deep,
              letterSpacing: '0.02em',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {`${rows.map((r) => `$${r.precioUsd}`).join(' + ')} = $${TOTAL_USD} `}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.95em' }}>
              USD/mes
            </Box>
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1.5, maxWidth: 720, lineHeight: 1.75 }}>
            Para una firma de esta categoría, el <strong>Plan Oro</strong> maximiza prestigio y retorno dentro de{' '}
            <strong>Legalmente en Familia</strong>: presencia total en audio, video, redes y editorial.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {rows.map(({ servicio, detalle, beneficio, Icon, precioUsd }) => (
          <Grid item xs={12} sm={6} key={servicio}>
            <Box
              sx={{
                height: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid rgba(196, 164, 93, 0.35)`,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(27,51,38,0.5)' : '#fff',
                boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 30px rgba(27,51,38,0.07)',
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)' },
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  pt: 2.5,
                  pb: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  background:
                    theme.palette.mode === 'dark'
                      ? `linear-gradient(120deg, rgba(196,164,93,0.12) 0%, transparent 55%)`
                      : `linear-gradient(120deg, rgba(27,51,38,0.06) 0%, transparent 50%)`,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: deep,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(196,164,93,0.25)',
                  }}
                >
                  <Icon sx={{ fontSize: 26 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: h, lineHeight: 1.3, pr: 1 }}>
                      {servicio}
                    </Typography>
                    <Box
                      sx={{
                        flexShrink: 0,
                        px: 1.25,
                        py: 0.65,
                        borderRadius: 2,
                        border: `1px solid rgba(196, 164, 93, 0.55)`,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(196,164,93,0.12)' : 'rgba(196,164,93,0.16)',
                        textAlign: 'right',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: gold, lineHeight: 1.2 }}>
                        ${precioUsd}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        USD / mes
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      mt: 1,
                      pl: 2.25,
                      listStyleType: 'disc',
                      color: 'text.secondary',
                      '& li': {
                        mb: 0.75,
                        lineHeight: 1.65,
                        pl: 0.35,
                      },
                      '& li:last-of-type': { mb: 0 },
                    }}
                  >
                    {detalle.map((item) => (
                      <Typography key={item} component="li" variant="body2" color="textSecondary">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 'auto',
                  px: 2.5,
                  py: 2,
                  borderTop: `1px solid rgba(196, 164, 93, 0.25)`,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(196,164,93,0.06)' : 'rgba(196,164,93,0.08)',
                }}
              >
                <Typography variant="caption" sx={{ color: gold, fontWeight: 700, letterSpacing: '0.08em' }}>
                  Beneficio
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: h, mt: 0.5, lineHeight: 1.55 }}>
                  {beneficio}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mt: 4,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(115deg, ${deep} 0%, ${theme.palette.primary.dark} 45%, #0f1a14 100%)`,
          border: `1px solid rgba(196, 164, 93, 0.45)`,
          boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: '8%',
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(196,164,93,0.25) 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={7}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
              Inversión mensual estimada
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: gold,
                fontWeight: 800,
                mt: 1,
                letterSpacing: '-0.02em',
                textShadow: '0 2px 24px rgba(0,0,0,0.35)',
              }}
            >
              {`$${TOTAL_USD}`}{' '}
              <Box component="span" sx={{ fontSize: '1.1rem', fontWeight: 600, opacity: 0.95 }}>USD · cash</Box>
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)', mt: 1.25, lineHeight: 1.6 }}>
              {rows.map((r) => `$${r.precioUsd}`).join(' + ')} = <strong style={{ color: gold }}>${TOTAL_USD} USD</strong>{' '}
              al mes (partidas del Plan Oro).
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.88)', lineHeight: 1.75 }}>
              Contratación mínima sugerida de <strong style={{ color: gold }}>3 meses</strong> para consolidar el efecto
              de marca y la presencia en la bóveda digital TNO: su mensaje sigue disponible On-Demand después de cada
              emisión.
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <PriceVatDisclaimer />
    </Box>
  );
};

export default PlanOroInvestment;
